import {
  ElementIndexContextProvider,
  ElementType,
  useQueryHistoryStateContext,
  useQueryStateContext,
  useVisualComponentsContext,
} from '../../contexts';
import { EventSelect as EventSelectRenderer } from '../EventSelect';
import { UserProfileSelect as UserProfileSelectRenderer } from '../requirement/UserProfileSelect';
import {
  useCriteriaCombinatorState,
  useCriteriaRequirementsState,
  useHasCriteriaCombinatorError,
} from '../../hooks/criteria';
import { ClientDataType, Combinator, EditableRequirement } from '../../types';
import { CombinatorInput } from './CombinatorInput';
import { DuplicateButton } from './DuplicateButton';
import { RequirementRenderer } from '../requirement/RequirementRenderer';
import { RemoveButton } from './RemoveButton';
import { useAddRequirementFromSelect } from '../../hooks/requirement/useAddRequirementFromSelect';
import { useIsCatalogESIndex } from '../../hooks/useIsCatalogESIndex';

export const CriteriaRenderer = ({
  criteriaAmount,
  isFirstElement,
  queryCombinator,
}: {
  criteriaAmount: number;
  isFirstElement: boolean;
  queryCombinator: Combinator;
}) => {
  const { CriteriaLayout } = useVisualComponentsContext();
  const { requirements } = useCriteriaRequirementsState();
  const { combinator = Combinator.AND, setCombinator } = useCriteriaCombinatorState();
  const addRequirementFromSelect = useAddRequirementFromSelect();
  const hasCriteriaCombinator = useHasCriteriaCombinatorError(combinator);
  const { isReadOnly = false } = useQueryStateContext();
  const { resetRedoStack } = useQueryHistoryStateContext();
  const isCatalogESIndex = useIsCatalogESIndex();

  const CombinatorSelect = (
    <CombinatorInput
      value={combinator}
      onChange={(updatedCombinator) => {
        if (updatedCombinator) setCombinator(updatedCombinator);
      }}
      isInvalid={hasCriteriaCombinator}
    />
  );

  const EventSelect = (
    <EventSelectRenderer
      value={null}
      onChange={(dataType) => {
        if (dataType) {
          addRequirementFromSelect({ dataType });
          resetRedoStack();
        }
      }}
    />
  );

  const UserProfileSelect = (
    <UserProfileSelectRenderer
      value={null}
      onChange={(userProfileField) => {
        if (userProfileField) {
          addRequirementFromSelect({ dataType: ClientDataType.User, userProfileField });
          resetRedoStack();
        }
      }}
    />
  );

  const Requirements = requirements.map((requirement: EditableRequirement, index: number) => (
    <ElementIndexContextProvider key={requirement.id} index={index} type={ElementType.Requirement}>
      <RequirementRenderer />
    </ElementIndexContextProvider>
  ));

  const shouldShowDelete = criteriaAmount > 1;

  return (
    <CriteriaLayout
      combinator={combinator}
      CombinatorSelect={CombinatorSelect}
      Delete={shouldShowDelete && <RemoveButton />}
      EventSelect={!isCatalogESIndex && EventSelect}
      isFirstElement={isFirstElement}
      isReadOnly={isReadOnly}
      queryCombinator={queryCombinator}
      Requirements={Requirements}
      UserProfileSelect={UserProfileSelect}
      Duplicate={<DuplicateButton />}
    />
  );
};
